import { Box, Stack, Typography, useTheme } from "@mui/material";
import React from "react";

interface Props {
  minHeight?: number;
  minWidth?: number;
  message?: string;
}

// Flat-top hexagon points for an SVG viewBox of 100x100, centered at (50,50)
const HEX_RADIUS = 40;
const HEX_POINTS = Array.from({ length: 6 }, (_, i) => {
  const angle = (Math.PI / 3) * i;
  return `${50 + HEX_RADIUS * Math.cos(angle)},${
    50 + HEX_RADIUS * Math.sin(angle)
  }`;
}).join(" ");

// Perimeter of the hexagon (6 sides × side length)
const HEX_PERIMETER = 6 * HEX_RADIUS;

export function LoadingSpinner(props: Props) {
  const { minHeight = 152, minWidth = 152, message } = props;
  const theme = useTheme();
  const accentColor = theme.palette.accent ?? theme.palette.primary.main;

  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        flexDirection: "row"
      }}
      style={{ minHeight, minWidth }}
    >
      <Stack spacing={2} justifyContent="center" alignItems="center">
        <Box
          role="progressbar"
          aria-label={message ?? "Loading"}
          className="site-motion"
          sx={{
            position: "relative",
            width: 100,
            height: 100,
            "@keyframes hexDraw": {
              "0%": {
                strokeDashoffset: HEX_PERIMETER
              },
              "50%": {
                strokeDashoffset: 0
              },
              "100%": {
                strokeDashoffset: -HEX_PERIMETER
              }
            },
            "@keyframes hexPulse": {
              "0%, 100%": { opacity: 0.3 },
              "50%": { opacity: 0.15 }
            },
            "@keyframes textFade": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.5 }
            }
          }}
        >
          <svg
            viewBox="0 0 100 100"
            width="100"
            height="100"
            style={{ position: "absolute", top: 0, left: 0 }}
          >
            {/* Background hex outline */}
            <polygon
              points={HEX_POINTS}
              fill="none"
              stroke={accentColor}
              strokeWidth="1.5"
              opacity="0.15"
              style={{ animation: "hexPulse 2s ease-in-out infinite" }}
            />
            {/* Animated drawing hex */}
            <polygon
              points={HEX_POINTS}
              fill="none"
              stroke={accentColor}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray={HEX_PERIMETER}
              strokeDashoffset={HEX_PERIMETER}
              style={{ animation: "hexDraw 2s ease-in-out infinite" }}
            />
          </svg>
          {/* SDB text centered */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: "textFade 2s ease-in-out infinite"
            }}
          >
            <Typography
              sx={{
                fontFamily: "'JetBrains Mono', monospace",
                fontWeight: 700,
                fontSize: "1.25rem",
                color: accentColor,
                letterSpacing: "0.1em",
                userSelect: "none"
              }}
            >
              SDB
            </Typography>
          </Box>
        </Box>
        {message ? <Typography>{message}</Typography> : null}
      </Stack>
    </Box>
  );
}
